/**
 * Google Sheets Transaction Repository
 *
 * Google Sheets API를 사용하여 실제 스프레드시트에서 거래 데이터를 관리합니다.
 */

import type {
  Transaction,
  TransactionInput,
  TransactionFilter,
} from '../types/Transaction'
import type { TransactionRepository } from './TransactionRepository'
import {
  GoogleSheetsClient,
  SheetsNetworkError,
  SheetsTimeoutError,
} from '@/core/api/GoogleSheetsClient'
import { SHEET_NAMES } from '@/core/config/constants'
import { logger } from '@/core/logger'
import { getWorkspaceClient } from '@/core/workspace/WorkspaceContext'
import { buildTransaction } from '../utils/buildTransaction'

/**
 * "요청이 서버에 도달했는지 알 수 없는" 실패인지 판정한다.
 *
 * @why 이 두 가지만 반영 여부 확인 대상이다. 4xx는 서버가 응답한 확정 실패라
 * 시트를 다시 읽어봐야 결과가 달라지지 않는다.
 */
function isResponseLostFailure(error: unknown): boolean {
  return error instanceof SheetsTimeoutError || error instanceof SheetsNetworkError
}

/**
 * Spreadsheet 행을 Transaction 객체로 변환
 *
 * 첫 행은 헤더이므로 2행부터 데이터입니다.
 */
function rowToTransaction(row: string[]): Transaction {
  return {
    id: row[0] || '',
    date: row[1] || '',
    type: (row[2] || 'expense') as 'income' | 'expense',
    majorCategoryId: row[3] || '',
    minorCategoryId: row[4] || null,
    amount: parseInt(row[5] || '0', 10),
    paymentMethodId: row[6] || '',
    spenderId: row[7] || '',
    writerId: row[8] || '',
    memo: row[9] || '',
    createdAt: row[10] || '',
    updatedAt: row[11] || '',
  }
}

/**
 * Transaction 객체를 Spreadsheet 행으로 변환
 */
function transactionToRow(transaction: Transaction): string[] {
  return [
    transaction.id,
    transaction.date,
    transaction.type,
    transaction.majorCategoryId,
    transaction.minorCategoryId || '',
    transaction.amount.toString(),
    transaction.paymentMethodId,
    transaction.spenderId,
    transaction.writerId,
    transaction.memo,
    transaction.createdAt,
    transaction.updatedAt,
  ]
}

/**
 * Google Sheets Transaction Repository 구현
 */
export class GoogleSheetsTransactionRepository implements TransactionRepository {
  constructor(_client?: GoogleSheetsClient) {
    // client 파라미터는 테스트 호환성을 위해 유지하지만 사용하지 않음
    // 실제로는 getClient()를 통해 워크스페이스 기반으로 클라이언트를 가져옴
  }

  /**
   * 현재 워크스페이스의 GoogleSheetsClient 가져오기
   *
   * 워크스페이스 전환 시 자동으로 올바른 스프레드시트에 접근합니다.
   */
  private getClient(): GoogleSheetsClient {
    return getWorkspaceClient()
  }

  /**
   * 모든 거래 조회 (필터 적용)
   *
   * Google Sheets는 서버 측 필터링이 약하므로
   * 전체 데이터를 가져와서 클라이언트에서 필터링합니다.
   */
  async findAll(filter?: TransactionFilter): Promise<Transaction[]> {
    // 시트에서 모든 데이터 읽기
    const rows = await this.getClient().readSheet(SHEET_NAMES.TRANSACTIONS)

    // 첫 행은 헤더이므로 제외
    const dataRows = rows.slice(1)

    // 각 행을 Transaction 객체로 변환
    // @why row.length > 0 만으로는 PUT 기반 hard-delete 시 남는 '빈 id' 행을
    // 거르지 못해 유령 row가 findAll에 섞여 들어옴. id(row[0])가 비어있으면 제외.
    let transactions = dataRows
      .filter((row) => row.length > 0 && Boolean(row[0]))
      .map(rowToTransaction)

    // 필터 적용 (MockRepository와 동일한 로직)
    if (filter) {
      if (filter.startDate) {
        transactions = transactions.filter((tx) => tx.date >= filter.startDate!)
      }
      if (filter.endDate) {
        transactions = transactions.filter((tx) => tx.date <= filter.endDate!)
      }
      if (filter.type) {
        transactions = transactions.filter((tx) => tx.type === filter.type)
      }
      // 카테고리 필터 (단일 또는 다중 선택)
      if (filter.majorCategoryId) {
        transactions = transactions.filter(
          (tx) => tx.majorCategoryId === filter.majorCategoryId
        )
      }
      if (filter.majorCategoryIds && filter.majorCategoryIds.length > 0) {
        transactions = transactions.filter((tx) =>
          filter.majorCategoryIds!.includes(tx.majorCategoryId)
        )
      }
      if (filter.minorCategoryId) {
        transactions = transactions.filter(
          (tx) => tx.minorCategoryId === filter.minorCategoryId
        )
      }
      if (filter.minorCategoryIds && filter.minorCategoryIds.length > 0) {
        transactions = transactions.filter((tx) =>
          tx.minorCategoryId && filter.minorCategoryIds!.includes(tx.minorCategoryId)
        )
      }

      // 사용자 필터 (단일 또는 다중 선택)
      if (filter.spenderId) {
        transactions = transactions.filter((tx) => tx.spenderId === filter.spenderId)
      }
      if (filter.spenderIds && filter.spenderIds.length > 0) {
        transactions = transactions.filter((tx) =>
          filter.spenderIds!.includes(tx.spenderId)
        )
      }
      if (filter.writerId) {
        transactions = transactions.filter((tx) => tx.writerId === filter.writerId)
      }
      if (filter.writerIds && filter.writerIds.length > 0) {
        transactions = transactions.filter((tx) =>
          filter.writerIds!.includes(tx.writerId)
        )
      }

      // 결제수단 필터 (단일 또는 다중 선택)
      if (filter.paymentMethodId) {
        transactions = transactions.filter(
          (tx) => tx.paymentMethodId === filter.paymentMethodId
        )
      }
      if (filter.paymentMethodIds && filter.paymentMethodIds.length > 0) {
        transactions = transactions.filter((tx) =>
          filter.paymentMethodIds!.includes(tx.paymentMethodId)
        )
      }
      if (filter.search) {
        const searchLower = filter.search.toLowerCase()
        transactions = transactions.filter((tx) =>
          tx.memo.toLowerCase().includes(searchLower)
        )
      }
    }

    // 날짜 내림차순 정렬 (최신순)
    transactions.sort((a, b) => b.date.localeCompare(a.date))

    return transactions
  }

  /**
   * ID로 거래 조회
   */
  async findById(id: string): Promise<Transaction | null> {
    const transactions = await this.findAll()
    return transactions.find((tx) => tx.id === id) || null
  }

  /**
   * 거래 생성
   *
   * @why 다건 경로에 위임해 ID 생성·엔티티 조립·컬럼 매핑이 단건과 다건에서
   * 갈라지지 않게 한다. 단건도 UUID 기반 ID를 함께 얻는다.
   */
  async create(input: TransactionInput): Promise<Transaction> {
    const [created] = await this.createMany([input])
    return created!
  }

  /**
   * 거래 일괄 생성
   *
   * @why appendSheet는 여러 행을 한 요청에 실을 수 있다. 건수만큼 요청을 보내면
   * 쓰기 할당량(60/분)을 빠르게 소모하고 중간 실패 시 일부만 저장된 상태가 남는데,
   * 1요청으로 묶으면 둘 다 사라진다.
   *
   * 동시 사용자 안전성: values.append는 클라이언트가 행 번호를 지정하지 않고
   * 서버가 표의 마지막 행 뒤에 덧붙인다. 그래서 두 사람이 동시에 저장해도
   * 서로의 행을 덮어쓰지 않는다. (행 번호를 계산해 PUT하는 방식으로 바꾸면
   * 이 안전성이 깨지므로 주의)
   */
  async createMany(inputs: TransactionInput[]): Promise<Transaction[]> {
    // 빈 배열에 append를 호출하면 의미 없는 API 요청만 발생한다
    if (inputs.length === 0) return []

    // TODO: writerId는 현재 spenderId로 대체된다(buildTransaction 기본 동작).
    //       로그인 사용자 연동 시 buildTransaction의 writerId 옵션을 채우면 된다.
    const created = inputs.map((input) => buildTransaction(input))

    const rows = created.map(transactionToRow)

    try {
      await this.getClient().appendSheet(SHEET_NAMES.TRANSACTIONS, rows)
      return created
    } catch (error) {
      if (!isResponseLostFailure(error)) {
        // 인증·권한·범위 오류 등 확정 실패. 다시 보내도 결과가 같으므로 그대로 알린다.
        throw error
      }

      return await this.recoverFromLostResponse(created, rows, error)
    }
  }

  /**
   * 응답이 유실된 쓰기를 되살린다.
   *
   * @why 타임아웃·네트워크 단절은 "요청이 서버에 안 갔다"와 "서버는 기록했는데
   * 응답만 못 받았다"를 구분해주지 않는다. 그냥 재전송하면 후자에서 거래가
   * 두 벌 쌓이고, 그냥 실패로 처리하면 전자에서 사용자가 입력을 통째로 다시 해야 한다.
   *
   * ID를 append 전에 클라이언트에서 만들어 두기 때문에, 시트를 한 번 읽어
   * 그 ID가 있는지 보면 둘을 확실히 구분할 수 있다.
   *
   * 확인 자체가 실패하면 재전송하지 않고 원래 에러를 던진다.
   * (모르는 상태에서의 재전송은 중복 저장이라는 되돌리기 어려운 손상을 만든다)
   */
  private async recoverFromLostResponse(
    created: Transaction[],
    rows: string[][],
    originalError: unknown
  ): Promise<Transaction[]> {
    const probeId = created[0]!.id

    let alreadyApplied: boolean
    try {
      const sheetRows = await this.getClient().readSheet(SHEET_NAMES.TRANSACTIONS)
      alreadyApplied = sheetRows.some((row) => row[0] === probeId)
    } catch (verifyError) {
      logger.error(
        '[TransactionRepository] 저장 반영 여부 확인 실패 — 중복 저장을 막기 위해 재전송하지 않습니다',
        verifyError
      )
      throw originalError
    }

    if (alreadyApplied) {
      logger.warn(
        '[TransactionRepository] 응답만 유실됐고 시트에는 이미 반영됨 — 재전송하지 않습니다'
      )
      return created
    }

    // 반영되지 않았음이 확인됐으므로 같은 행을 다시 보내도 중복되지 않는다
    logger.warn('[TransactionRepository] 저장이 반영되지 않아 한 번 더 전송합니다')
    await this.getClient().appendSheet(SHEET_NAMES.TRANSACTIONS, rows)

    return created
  }

  /**
   * 거래 수정
   */
  async update(id: string, input: Partial<TransactionInput>): Promise<Transaction> {
    // 전체 데이터를 읽어서 해당 거래 찾기
    const rows = await this.getClient().readSheet(SHEET_NAMES.TRANSACTIONS)
    const dataRows = rows.slice(1)

    const rowIndex = dataRows.findIndex((row) => row[0] === id)

    if (rowIndex === -1) {
      throw new Error(`거래를 찾을 수 없습니다: ${id}`)
    }

    const existingRow = dataRows[rowIndex]
    if (!existingRow) {
      throw new Error(`거래 행 데이터를 찾을 수 없습니다: ${id}`)
    }

    // 기존 데이터와 병합
    const existingTransaction = rowToTransaction(existingRow)
    const updated: Transaction = {
      ...existingTransaction,
      ...input,
      updatedAt: new Date().toISOString(),
    }

    // 시트에서 해당 행 업데이트 (2행부터 시작이므로 +2)
    const sheetRowIndex = rowIndex + 2
    const range = `A${sheetRowIndex}:L${sheetRowIndex}`
    const row = transactionToRow(updated)
    await this.getClient().updateSheet(SHEET_NAMES.TRANSACTIONS, range, [row])

    return updated
  }

  /**
   * 거래 삭제 (Hard Delete)
   *
   * Google Sheets에서 해당 행을 실제로 제거합니다.
   *
   * @why Sheets API의 values.update(PUT)는 전달한 range 안의 셀만 덮어쓰고
   * 그 너머의 셀은 그대로 둡니다. 따라서 원본보다 짧은 range로 쓰면
   * 마지막 한 행이 유령처럼 남아 findAll에 다시 섞여 들어오는 버그가
   * 있었습니다. 원본과 동일한 행 수만큼 빈 행을 패딩해 마지막 row까지
   * 확실히 비우고, findAll에서는 id가 빈 row를 무시합니다.
   */
  async delete(id: string): Promise<void> {
    const rows = await this.getClient().readSheet(SHEET_NAMES.TRANSACTIONS)

    if (rows.length === 0) {
      throw new Error('시트에 데이터가 없습니다')
    }

    const header = rows[0]!
    const dataRows = rows.slice(1)

    const filteredRows = dataRows.filter((row) => row[0] !== id)

    if (filteredRows.length === dataRows.length) {
      throw new Error(`거래를 찾을 수 없습니다: ${id}`)
    }

    // 원본 길이만큼 빈 행으로 패딩하여 트레일링 row가 남지 않도록 함
    const COLUMN_COUNT = 12 // A..L
    const emptyRow = Array<string>(COLUMN_COUNT).fill('')
    const padded: string[][] = [...filteredRows]
    while (padded.length < dataRows.length) {
      padded.push([...emptyRow])
    }

    const range = `A1:L${dataRows.length + 1}`
    await this.getClient().updateSheet(
      SHEET_NAMES.TRANSACTIONS,
      range,
      [header, ...padded]
    )
  }
}
